import { useState, useEffect } from 'react';
import {
  Button,
  Badge,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Spinner,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@rnd-payplus/alloy-react';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { Plus, Pencil, Trash2, Pause, Play, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { FilterButton, SearchFilterButton } from '../components/FilterButton';
import { getCurrentTenantId } from '../utils/auth';
import { PLATFORMS } from '../constants/platforms';
import { usePageShell } from '../contexts/PageShellContext';

interface Integration {
  id: string;
  tenant: string;
  erp: string;
  type: string;
  status: 'active' | 'degraded' | 'error' | 'paused';
  lastSync: string;
  errors24h: number;
  throughput: number;
  uptime: number;
  endpoint?: string;
}

const platforms = PLATFORMS;

function formatRelativeTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function getStatusBadgeVariant(status: Integration['status']): 'success' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active': return 'success';
    case 'degraded': return 'secondary';
    case 'error': return 'destructive';
    case 'paused': return 'outline';
  }
}

const IntegrationsPage = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState<string[]>([]);
  const [erpFilter, setErpFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    platform: '',
    name: '',
    endpoint: '',
    apiKey: '',
  });

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/connections');
      if (!response.ok) {
        throw new Error(`Failed to load integrations (${response.status})`);
      }
      const data = await response.json();
      const mapped: Integration[] = data.map((c: any) => ({
        id: c.id,
        tenant: c.tenant || 'Default Tenant',
        erp: c.platform || 'Unknown',
        type: c.type || 'Payment',
        status: c.status === 'connected' ? 'active' : c.status === 'error' ? 'error' : 'degraded',
        lastSync: c.last_tested || new Date().toISOString(),
        errors24h: c.errors_24h || 0,
        throughput: c.throughput || 0,
        uptime: c.uptime || 99.0,
        endpoint: c.endpoint,
      }));
      setIntegrations(mapped);
    } catch (err) {
      setIntegrations([]);
      toast.error(err instanceof Error ? err.message : 'Failed to load integrations');
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.platform) {
      toast.error('Name and Platform are required');
      return;
    }

    try {
      const response = await fetch('/api/connections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          tenantId: getCurrentTenantId('default'),
          status: 'disconnected',
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create integration (${response.status})`);
      }

      const result = await response.json();
      toast.success(result.created ? 'Integration created successfully!' : 'Integration updated successfully!');
      setDialogOpen(false);
      setFormData({ platform: '', name: '', endpoint: '', apiKey: '' });
      fetchIntegrations();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create integration');
    }
  };

  const handleEdit = async () => {
    if (!selectedIntegration) return;

    try {
      const response = await fetch(`/api/connections/${selectedIntegration.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update integration (${response.status})`);
      }

      toast.success('Integration updated!');
      setEditDialogOpen(false);
      fetchIntegrations();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update integration');
    }
  };

  const handleDelete = async () => {
    if (!selectedIntegration) return;

    try {
      const response = await fetch(`/api/connections/${selectedIntegration.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete integration (${response.status})`);
      }

      toast.success('Integration deleted!');
      setDeleteDialogOpen(false);
      setIntegrations(integrations.filter(i => i.id !== selectedIntegration.id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete integration');
    }
  };

  const filtered = integrations.filter((i) => {
    if (statusFilter.length > 0 && !statusFilter.includes(i.status)) return false;
    if (tenantFilter.length > 0 && !tenantFilter.includes(i.tenant)) return false;
    if (erpFilter.length > 0 && !erpFilter.includes(i.erp)) return false;
    if (typeFilter.length > 0 && !typeFilter.includes(i.type)) return false;
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      if (
        !i.tenant.toLowerCase().includes(q) &&
        !i.erp.toLowerCase().includes(q) &&
        !i.type.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  const total = integrations.length;
  const active = integrations.filter(i => i.status === 'active').length;
  const degraded = integrations.filter(i => i.status === 'degraded').length;
  const error = integrations.filter(i => i.status === 'error').length;

  usePageShell({
    title: 'Integrations',
    primaryAction: (
      <Button onClick={() => setDialogOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Add Integration
      </Button>
    ),
    secondaryActions: (
      <Button variant="outline" onClick={fetchIntegrations}>
        <RefreshCcw className="mr-2 h-4 w-4" />
        Refresh
      </Button>
    ),
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Integrations' },
    ],
  });

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Total" value={total} subtitle="Integrations" />
        <KPICard title="Active" value={active} subtitle="Integrations" />
        <KPICard title="Degraded" value={degraded} subtitle="Integrations" />
        <KPICard title="Error" value={error} subtitle="Integrations" />
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2 mb-4">
        <FilterButton
          label="Status"
          options={['active', 'degraded', 'error', 'paused']}
          selected={statusFilter}
          onChange={setStatusFilter}
        />
        <SearchFilterButton
          label="Search"
          value={searchFilter}
          onChange={setSearchFilter}
        />
        <FilterButton
          label="Tenant"
          options={[...new Set(integrations.map((i) => i.tenant))]}
          selected={tenantFilter}
          onChange={setTenantFilter}
        />
        <FilterButton
          label="ERP"
          options={[...new Set(integrations.map((i) => i.erp))]}
          selected={erpFilter}
          onChange={setErpFilter}
        />
        <FilterButton
          label="Type"
          options={[...new Set(integrations.map((i) => i.type))]}
          selected={typeFilter}
          onChange={setTypeFilter}
        />
      </div>

      {/* Data Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tenant</TableHead>
            <TableHead>ERP</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Last Sync</TableHead>
            <TableHead>Errors/24h</TableHead>
            <TableHead>Throughput</TableHead>
            <TableHead>Uptime</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((integration) => (
            <TableRow key={integration.id}>
              <TableCell className="font-medium">{integration.tenant}</TableCell>
              <TableCell>{integration.erp}</TableCell>
              <TableCell>{integration.type}</TableCell>
              <TableCell>
                <Badge variant={getStatusBadgeVariant(integration.status)}>
                  {integration.status}
                </Badge>
              </TableCell>
              <TableCell>{formatRelativeTime(integration.lastSync)}</TableCell>
              <TableCell>
                <span className={integration.errors24h > 0 ? 'text-destructive font-medium' : ''}>
                  {integration.errors24h}
                </span>
              </TableCell>
              <TableCell>{integration.throughput}/hr</TableCell>
              <TableCell>
                <span className={integration.uptime >= 95 ? 'text-green-600' : 'text-destructive'}>
                  {integration.uptime}%
                </span>
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Edit integration"
                    aria-label="Edit integration"
                    onClick={() => {
                      setSelectedIntegration(integration);
                      setFormData({
                        platform: integration.erp,
                        name: '',
                        endpoint: integration.endpoint ?? '',
                        apiKey: '',
                      });
                      setEditDialogOpen(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title={integration.status === 'paused' ? 'Resume integration' : 'Pause integration'}
                    aria-label={integration.status === 'paused' ? 'Resume integration' : 'Pause integration'}
                    onClick={async () => {
                      const nextStatus = integration.status === 'paused' ? 'active' : 'paused';
                      try {
                        const response = await fetch(`/api/connections/${integration.id}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ status: nextStatus }),
                        });
                        if (!response.ok) {
                          throw new Error(`Failed to ${nextStatus === 'paused' ? 'pause' : 'resume'} integration (${response.status})`);
                        }
                        toast.success(`Integration ${nextStatus === 'paused' ? 'paused' : 'resumed'}`);
                        fetchIntegrations();
                      } catch (err) {
                        toast.error(err instanceof Error ? err.message : 'Failed to update integration status');
                      }
                    }}
                  >
                    {integration.status === 'paused'
                      ? <Play className="h-4 w-4" />
                      : <Pause className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    title="Delete integration"
                    aria-label="Delete integration"
                    onClick={() => {
                      setSelectedIntegration(integration);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Integration</DialogTitle>
            <DialogDescription>Configure a new platform integration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Platform *</Label>
              <Select value={formData.platform} onValueChange={(val) => setFormData({ ...formData, platform: val })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a platform" />
                </SelectTrigger>
                <SelectContent>
                  {platforms.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-name">Connection Name *</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-endpoint">API Endpoint</Label>
              <Input
                id="create-endpoint"
                value={formData.endpoint}
                onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })}
                placeholder="https://api.example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-apikey">API Key</Label>
              <Input
                id="create-apikey"
                type="password"
                value={formData.apiKey}
                onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleCreate}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Integration</DialogTitle>
            <DialogDescription>Update the integration settings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Connection Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-endpoint">API Endpoint</Label>
              <Input
                id="edit-endpoint"
                value={formData.endpoint}
                onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Integration</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <p className="py-4">
            Are you sure you want to delete this integration? This cannot be undone.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationsPage;
